import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@rio-cloud/rio-uikit/Button';
import classNames from '@rio-cloud/rio-uikit/classNames';
import DayPicker, { DateRange, DayPickerSlotRenderProps } from '@rio-cloud/rio-uikit/DayPicker';
import { isMobileScreen } from '@rio-cloud/rio-uikit/DeviceUtils';

import { FILTER_MSG } from '@/features/maintenance-data/filters/constants';
import { DueAtRangeType } from '@/features/maintenance-data/hooks/useMaintenanceDataQueryParams';
import {
    ComputablePreset,
    DateRangeValue,
    fromIsoRange,
    getPresetRange,
    toIsoRange,
} from '@/features/maintenance-data/utils/dueAtPresets';

export interface DueAtChange {
    dueDateFrom?: string;
    dueDateTo?: string;
    dueAtRangeType?: DueAtRangeType;
}

interface MaintenanceDueAtFilterProps {
    dueDateFrom?: string;
    dueDateTo?: string;
    dueAtRangeType?: DueAtRangeType;
    onChange: (change: DueAtChange) => void;
    /** Optionally render a Clear action in the footer (the input also exposes an inline clear via isClearable). */
    showClearButton?: boolean;
    disabled?: boolean;
}

const PRESETS: { preset: ComputablePreset; labelKey: string }[] = [
    { preset: 'TODAY', labelKey: `${FILTER_MSG}.preset.today` },
    { preset: 'NEXT_WEEK', labelKey: `${FILTER_MSG}.preset.nextWeek` },
    { preset: 'NEXT_MONTH', labelKey: `${FILTER_MSG}.preset.nextMonth` },
];

const toDayPickerRange = (range: DateRangeValue): DateRange => ({ from: range.from, to: range.to });

const sameTime = (a?: Date, b?: Date): boolean => a?.getTime() === b?.getTime();

const rangesEqual = (a: DateRange | undefined, b: DateRange): boolean =>
    sameTime(a?.from, b.from) && sameTime(a?.to, b.to);

/** Returns the computable preset whose freshly-computed range equals [range], or undefined. */
const matchPreset = (range: DateRange | undefined): ComputablePreset | undefined =>
    PRESETS.find(({ preset }) => rangesEqual(range, toDayPickerRange(getPresetRange(preset))))?.preset;

/**
 * Due-at range filter: a single DayPicker (range mode, deferred) with preset buttons in the left
 * sidebar and Cancel/Apply (optionally Clear) footer actions. The input is clearable via the
 * inline ✕. Presets are computed client-side; the committed range is emitted as ISO strings plus
 * the chosen preset type (CUSTOM when a range is set, undefined when cleared).
 */
export const MaintenanceDueAtFilter: React.FC<MaintenanceDueAtFilterProps> = ({
    dueDateFrom,
    dueDateTo,
    dueAtRangeType,
    onChange,
    showClearButton = false,
    disabled = false,
}) => {
    const intl = useIntl();
    const value = toDayPickerRange(fromIsoRange(dueDateFrom, dueDateTo));

    // Fired by DayPicker on apply()/clear() with the committed range. The persisted preset type is
    // derived from the range itself: if it equals a freshly-computed preset range it's that preset,
    // otherwise CUSTOM; an empty range clears the filter.
    const handleChange = (range: DateRange | undefined) => {
        if (!range?.from && !range?.to) {
            onChange({ dueDateFrom: undefined, dueDateTo: undefined, dueAtRangeType: undefined });
            return;
        }
        const iso = toIsoRange({ from: range?.from, to: range?.to });
        onChange({ dueDateFrom: iso.from, dueDateTo: iso.to, dueAtRangeType: matchPreset(range) ?? 'CUSTOM' });
    };

    const handleLeftSidebar = useCallback(({ setDraftValue, draftValue }: DayPickerSlotRenderProps<DateRange>) => {
        const activePreset = matchPreset(draftValue);
        return (
            <ul className="nav nav-pills nav-stacked nav-pills-stacked-decent">
                {PRESETS.map(({ preset, labelKey }) => (
                    <li
                        key={preset}
                        className={classNames(activePreset === preset && 'active')}
                        onClick={() => setDraftValue(toDayPickerRange(getPresetRange(preset)))}
                    >
                        <span>
                            <FormattedMessage id={labelKey} />
                        </span>
                    </li>
                ))}
            </ul>
        );
    }, []);
    const handleFooter = useCallback(
        ({ apply, clear, close }: DayPickerSlotRenderProps<DateRange>) => (
            <div className="display-flex justify-content-between gap-10">
                <div>
                    {showClearButton && (
                        <Button onClick={clear}>
                            <FormattedMessage id="intl-msg:vehicle-diagnostics.button.reset" />
                        </Button>
                    )}
                </div>
                <div className="display-flex gap-10">
                    <Button bsStyle="muted" onClick={close}>
                        <FormattedMessage id="intl-msg:vehicle-diagnostics.button.cancel" />
                    </Button>
                    <Button bsStyle="primary" onClick={apply}>
                        <FormattedMessage id="intl-msg:vehicle-diagnostics.button.apply" />
                    </Button>
                </div>
            </div>
        ),
        [showClearButton],
    );
    return (
        <div className="display-flex flex-column min-width-200">
            <span className="text-color-highlight-light margin-bottom-5">
                <FormattedMessage id={`${FILTER_MSG}.dueAt.title`} />
            </span>
            <DayPicker
                mode="range"
                selectionBehavior="deferred"
                numberOfMonths={2}
                locale={intl.locale}
                isClearable
                usePortal
                closeOnSelect={false}
                showOutsideDays={false}
                inputProps={{ disabled }}
                value={value.from || value.to ? value : undefined}
                placeholder={intl.formatMessage({ id: `${FILTER_MSG}.placeholder` })}
                onChange={handleChange}
                leftSidebarClassName={classNames(
                    'min-width-150',
                    !isMobileScreen() && 'border border-color-lighter border-right-only',
                )}
                leftSidebar={handleLeftSidebar}
                footerClassName="padding-15 border border-top-only border-color-lighter"
                footer={handleFooter}
            />
        </div>
    );
};

export default MaintenanceDueAtFilter;
